import { observer } from "mobx-react-lite";
import { useAppStore, useGuestAppStore } from "@/store";
import { EntityNotFoundType, NotFoundPage } from "@/pages/not-found/NotFoundPage";
import { StandardModePadding } from "@/app/outlets/StandardModePadding";
import { INotesViewPageStore } from "@/store/pages/NotesViewPageStore/types";
import { NoteEditor } from "@/pages/notes-view/NoteEditor";
import { AddToCollectionModal } from "@/components/modal/add-to-collection/add-to-collection-modal";
import { ChatHistory } from "@/store/chat/ChatHistory";
import { useTabLocationState } from "@/hooks/useTabLocationState";
import { FileUploadRejectedModal } from "@/components/modal/file-upload-rejected/file-upload-rejeted-modal";
import { ImageUploadRejectedModal } from "@/components/modal/image-upload-rejected/image-upload-rejected-modal";
import { useLiveNoteSubscription } from "@/components/note/use-live-note-subscription";
import { NoteContentVersion } from "@/store/note/types";
import useDebouncedCallback from "@/domains/react/useDebouncedCallback";
import { useTabPageStore } from "@/hooks/useTabPageStore";
import { NotesViewPageStore } from "@/store/pages/NotesViewPageStore/NotesViewPageStore";
import { useTabContext } from "@/components/layout/TabContext";
import { useEffect } from "react";

export const StandardNotesViewPage = observer(function StandardNotesViewPage() {
  const store = useTabPageStore("notesViewPage");
  if (!store) return null;
  return <StandardNotesViewPageInternal page={store} />;
});

export const StandardNotesViewPageInternal = observer(function StandardNotesViewPageInternal({
  page,
}: {
  page: NotesViewPageStore;
}) {
  const { store } = useAppStore();
  page.useEffects();
  useLiveNoteSubscription(page.noteId);

  const routerLocationState = useTabLocationState();
  // react to history version in router location state
  useEffect(() => {
    const routerHistoryVersion = routerLocationState?.historyVersion;
    const noteId = page.itemObservable?.id;
    if (!routerHistoryVersion || !noteId) return;

    const isAlreadyOpen =
      page.noteVersionHistory.currentNoteId === noteId &&
      page.noteVersionHistory.selectedSnapshot?.note_content_document_version ===
        routerHistoryVersion;
    if (isAlreadyOpen) return;

    page.noteVersionHistory.openNoteVersionHistory(noteId, routerHistoryVersion);
  }, [page.itemObservable?.id, page.noteVersionHistory, routerLocationState?.historyVersion]);

  return (
    <NotesViewPage
      chatHistory={store.chatMessages.chatHistory}
      store={page}
      isSidePanelOpen={store.interface.isChatSidebarOpen}
      versions={page.versions}
    />
  );
});

export const GuestNotesViewPage = observer(function GuestNotesViewPage() {
  const { guestStore } = useGuestAppStore();
  guestStore.notesViewPage.useNoteIdFromRoutePath();
  return <NotesViewPage store={guestStore.notesViewPage} />;
});

interface NotesViewPageProps {
  store: INotesViewPageStore;
  isSidePanelOpen?: boolean;
  chatHistory?: ChatHistory;
  versions?: NoteContentVersion[];
}

const NotesViewPage = observer<NotesViewPageProps>(
  ({ store, versions, isSidePanelOpen, chatHistory }) => {
    const { store: appStore } = useAppStore();
    const { activeTab } = useTabContext();
    const routerLocationState = useTabLocationState();

    const onNoteRendered = useDebouncedCallback(
      (noteId: string) => {
        if (!routerLocationState?.autoStartVoice || appStore.routing.noteIdParam !== noteId) {
          return;
        }

        // Start voice recording then clear the flag
        appStore.audioCapture.startRecording({ noteId });
        appStore.navigation.tabs.clearTabState(activeTab.id);
      },
      100,
      { leading: false, trailing: true }
    );

    if (store.showNotFound) {
      // Apply consistent padding for the NotFoundPage.
      return (
        <StandardModePadding>
          <NotFoundPage entityType={EntityNotFoundType.Note} />
        </StandardModePadding>
      );
    }

    return (
      <>
        {store.addToCollectionModal && <AddToCollectionModal store={store.addToCollectionModal} />}
        {store.fileUploadRejectedModal && (
          <FileUploadRejectedModal store={store.fileUploadRejectedModal} />
        )}
        {store.imageUploadRejectedModal && (
          <ImageUploadRejectedModal store={store.imageUploadRejectedModal} />
        )}
        <NoteEditor
          isSidePanelOpen={!!isSidePanelOpen}
          chatHistory={chatHistory}
          noteObservable={store.itemObservable}
          openFileUploadRejectedModal={store.openFileUploadRejectedModal}
          openImageUploadRejectedModal={store.openImageUploadRejectedModal}
          myAccount={store.myAccount}
          goToMention={store.goToMention}
          highlightText={routerLocationState?.highlightText}
          autoFocus={routerLocationState?.autoFocus}
          versions={versions}
          onNoteRendered={onNoteRendered}
        />
      </>
    );
  }
);
